// @bogas/launch-countdown: <launch-countdown data-launch> on the coming-soon page
// (sections/main-password.liquid): the section draws the days, hours, minutes and seconds left
// for the first paint, this ticks them each second, then swaps them for the "any minute now" line.
import { getRemainingMs, padTwo, splitDuration } from '@bogas/countdown';

/**
 * The count at `now`: two digits per unit, done at zero; null for a launch time that is no date.
 * @param {string} launchAt - ISO 8601 with its offset (the section's setting)
 * @param {number} now
 * @returns {{ done: boolean, parts: { days: string, hours: string, minutes: string, seconds: string } } | null}
 */
export function countdownParts(launchAt, now) {
  const left = getRemainingMs(launchAt, now);
  if (left === null) return null;
  const { days, hours, minutes, seconds } = splitDuration(left);
  return {
    done: Math.floor(left / 1000) <= 0,
    parts: { days: padTwo(days), hours: padTwo(hours), minutes: padTwo(minutes), seconds: padTwo(seconds) },
  };
}

// The element's base where there is a DOM; the helper above also loads in Node (unit tests).
const Element_ = typeof HTMLElement === 'undefined' ? class {} : HTMLElement;

class LaunchCountdown extends Element_ {
  #timer = 0;

  connectedCallback() {
    this.#tick();
  }

  disconnectedCallback() {
    clearTimeout(this.#timer);
  }

  #tick = () => {
    const count = countdownParts(this.dataset.launch ?? '', Date.now());
    if (!count) return;
    const units = this.querySelector('[data-launch-units]');
    const done = this.querySelector('[data-launch-done]');
    if (count.done) {
      if (units) units.hidden = true;
      if (done) done.hidden = false;
      return;
    }
    for (const cell of this.querySelectorAll('[data-unit]')) {
      const value = count.parts[cell.dataset.unit];
      if (value !== undefined && cell.textContent !== value) cell.textContent = value;
    }
    // On the second, so the seconds change together with the clock.
    this.#timer = window.setTimeout(this.#tick, 1000 - (Date.now() % 1000));
  };
}

if (typeof customElements !== 'undefined' && !customElements.get('launch-countdown')) {
  customElements.define('launch-countdown', LaunchCountdown);
}
